import { create } from 'zustand'

interface FocusedCell {
  rowId: string
  colId: string
}

interface DagTableState {
  computingCells: Set<string>
  computingStartedAt: Record<string, number>
  aggregates: Record<string, unknown>
  focusedCell: FocusedCell | null
  editingCell: FocusedCell | null
  highlightedRowId: string | null
  sseReconnectNonce: number

  setComputing: (rowId: string, columnIds: string[]) => void
  clearComputing: (rowId: string, columnId: string) => void
  clearAllComputing: (rowId: string) => void
  setAggregates: (aggregates: Record<string, unknown>) => void
  mergeAggregates: (partial: Record<string, unknown>) => void
  setFocusedCell: (rowId: string, colId: string) => void
  setEditingCell: (rowId: string, colId: string) => void
  clearFocus: () => void
  clearEditing: () => void
  setHighlightedRowId: (rowId: string | null) => void
  triggerSseReconnect: () => void
  reset: () => void
}

const cellKey = (rowId: string, colId: string) => `${rowId}:${colId}`

export const useDagTableStore = create<DagTableState>((set, get) => ({
  computingCells: new Set(),
  computingStartedAt: {},
  aggregates: {},
  focusedCell: null,
  editingCell: null,
  highlightedRowId: null,
  sseReconnectNonce: 0,

  setComputing: (rowId, columnIds) => {
    const next = new Set(get().computingCells)
    const startedAt = { ...get().computingStartedAt }
    const now = Date.now()
    for (const col of columnIds) {
      const key = cellKey(rowId, col)
      next.add(key)
      if (startedAt[key] === undefined) startedAt[key] = now
    }
    set({ computingCells: next, computingStartedAt: startedAt })
  },

  clearComputing: (rowId, columnId) => {
    const key = cellKey(rowId, columnId)
    const next = new Set(get().computingCells)
    const startedAt = { ...get().computingStartedAt }
    next.delete(key)
    delete startedAt[key]
    set({ computingCells: next, computingStartedAt: startedAt })
  },

  clearAllComputing: rowId => {
    const next = new Set(get().computingCells)
    const startedAt = { ...get().computingStartedAt }
    for (const key of next) {
      if (key.startsWith(`${rowId}:`)) {
        next.delete(key)
        delete startedAt[key]
      }
    }
    set({ computingCells: next, computingStartedAt: startedAt })
  },

  setAggregates: aggregates => set({ aggregates }),

  mergeAggregates: partial => {
    if (!partial || Object.keys(partial).length === 0) return
    set(state => ({ aggregates: { ...state.aggregates, ...partial } }))
  },

  setFocusedCell: (rowId, colId) =>
    set({ focusedCell: { rowId, colId }, editingCell: null }),

  setEditingCell: (rowId, colId) =>
    set({ focusedCell: { rowId, colId }, editingCell: { rowId, colId } }),

  clearFocus: () => set({ focusedCell: null, editingCell: null }),

  clearEditing: () => set({ editingCell: null }),

  setHighlightedRowId: rowId => set({ highlightedRowId: rowId }),

  triggerSseReconnect: () =>
    set(state => ({ sseReconnectNonce: state.sseReconnectNonce + 1 })),

  reset: () =>
    set({
      computingCells: new Set(),
      computingStartedAt: {},
      aggregates: {},
      focusedCell: null,
      editingCell: null,
      highlightedRowId: null,
    }),
}))
